import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const CustomSelect = ({
    options,
    value,
    onChange,
    placeholder = "Select Option",
    icon: Icon,
    disabled = false,
    disabledOptions = {}
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState(null);
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);

    // Get current label for display
    const currentLabel = options[value] || placeholder;

    const updateMenuPosition = () => {
        if (!dropdownRef.current) return;
        const rect = dropdownRef.current.getBoundingClientRect();
        setMenuStyle({
            position: "fixed",
            top: rect.bottom + 8,
            left: rect.left,
            width: rect.width,
            zIndex: 9999,
        });
    };

    useLayoutEffect(() => {
        if (!isOpen) return;
        updateMenuPosition();
        window.addEventListener("scroll", updateMenuPosition, true);
        window.addEventListener("resize", updateMenuPosition);
        return () => {
            window.removeEventListener("scroll", updateMenuPosition, true);
            window.removeEventListener("resize", updateMenuPosition);
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const inTrigger = dropdownRef.current?.contains(event.target);
            const inMenu = menuRef.current?.contains(event.target);
            if (!inTrigger && !inMenu) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (optionKey) => {
        if (disabledOptions?.[optionKey]) return;
        onChange({ target: { value: optionKey } });
        setIsOpen(false);
    };

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={`w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all group ${isOpen ? 'ring-2 ring-blue-500/20 border-blue-500' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                disabled={disabled}
            >
                <div className="flex items-center gap-2 flex-1">
                    <div className="flex items-center gap-3 bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent flex-1 overflow-hidden">
                        {Icon && <Icon className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors shrink-0" />}
                        <span className="truncate">
                            {currentLabel}
                        </span>
                    </div>
                </div>
                <div className="px-3 border-l border-gray-100 dark:border-white/10 ml-2">
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} />
                </div>
            </button>

            {createPortal(
                <AnimatePresence>
                    {isOpen && menuStyle && (
                        <motion.div
                            ref={menuRef}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            style={menuStyle}
                            className="bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 rounded-xl shadow-2xl overflow-hidden backdrop-blur-xl"
                        >
                            <div className="max-h-60 overflow-y-auto 
                                [&::-webkit-scrollbar]:w-1.5
                                [&::-webkit-scrollbar-track]:bg-transparent
                                [&::-webkit-scrollbar-thumb]:rounded-full
                                [&::-webkit-scrollbar-thumb]:bg-gray-200
                                dark:[&::-webkit-scrollbar-thumb]:bg-gray-800
                                hover:[&::-webkit-scrollbar-thumb]:bg-gray-300
                                dark:hover:[&::-webkit-scrollbar-thumb]:bg-gray-700"
                            >
                                {Object.entries(options).map(([key, label]) => {
                                    const isDisabled = Boolean(disabledOptions?.[key]);
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => handleSelect(key)}
                                            disabled={isDisabled}
                                            className={`w-full flex items-center justify-between px-4 py-3 text-sm transition-all ${isDisabled
                                                    ? "text-gray-400 dark:text-gray-600 cursor-not-allowed bg-transparent"
                                                    : `hover:bg-blue-50 dark:hover:bg-blue-900/20 ${String(value) === String(key)
                                                        ? 'bg-blue-50/50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold'
                                                        : 'text-gray-700 dark:text-gray-300'
                                                    }`
                                                }`}
                                        >
                                            <span className="truncate">{label}</span>
                                            {String(value) === String(key) && !isDisabled && (
                                                <Check className="w-4 h-4 text-blue-500 shrink-0" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div>
    );
};

export default CustomSelect;
